import { ClientResponseError } from "pocketbase";

/**
 * Error helpers shared by the data layer and the UI.
 *
 * Kobo is online-first: a dead backend must always read as "cannot reach the
 * Kobo server", never as an empty list. That distinction is made here once, so
 * no feature has to re-derive it from a raw error object.
 */

export function isClientResponseError(error: unknown): error is ClientResponseError {
  return error instanceof ClientResponseError;
}

/** The request was cancelled by us (navigation, auto-cancellation, unmount). */
export function isAbortError(error: unknown): boolean {
  if (isClientResponseError(error)) {
    return error.isAbort;
  }
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * The messages `fetch()` rejects with when the request never reached a server.
 *
 * Chrome and Node say "Failed to fetch" / "fetch failed", Firefox says
 * "NetworkError when attempting to fetch resource", WebKit says "Load failed",
 * and React Native says "Network request failed".
 */
const FETCH_FAILURE_MESSAGE = /failed to fetch|fetch failed|networkerror|load failed|network request failed/i;

/**
 * The server could not be reached at all — DNS failure, refused connection,
 * TLS error, offline device. PocketBase reports these as status 0.
 *
 * The bare-`TypeError` fallback covers `fetch()` calls made outside the SDK,
 * but it is matched on the message rather than on the type: `TypeError` is also
 * what a plain programming mistake throws ("Cannot read properties of
 * undefined"), and treating those as an outage makes `ErrorBoundary` describe
 * a render bug as "Cannot reach the Kobo server" — a diagnosis that sends
 * whoever reads it to the network instead of to the stack.
 */
export function isUnreachableError(error: unknown): boolean {
  if (isAbortError(error)) {
    return false;
  }
  if (isClientResponseError(error)) {
    return error.status === 0;
  }
  return error instanceof TypeError && FETCH_FAILURE_MESSAGE.test(error.message);
}

/** The session is missing, expired or not permitted to perform the request. */
export function isAuthError(error: unknown): boolean {
  return isClientResponseError(error) && (error.status === 401 || error.status === 403);
}

/** The request was rejected because the payload failed validation. */
export function isValidationError(error: unknown): boolean {
  return isClientResponseError(error) && error.status === 400;
}

/**
 * A sign-in attempt was refused.
 *
 * PocketBase answers a wrong email/password with 400 ("Failed to authenticate."),
 * not 401, so both statuses have to count — otherwise a mistyped password shows
 * the server's wording instead of something a human can act on.
 */
export function isBadCredentialsError(error: unknown): boolean {
  return isClientResponseError(error) && (error.status === 400 || error.status === 401);
}

/**
 * A human-readable, non-technical message for any error the app can produce.
 * Field-level validation details are folded in so a failed write explains
 * itself rather than showing a bare "Failed to create record".
 */
export function errorMessage(error: unknown): string {
  if (isUnreachableError(error)) {
    return "Cannot reach the Kobo server.";
  }

  if (isClientResponseError(error)) {
    const fieldMessages = Object.entries(error.response?.data ?? {})
      .map(([field, detail]) => {
        const message = (detail as { message?: string } | null)?.message;
        return message ? `${field}: ${message}` : null;
      })
      .filter((entry): entry is string => entry !== null);

    if (fieldMessages.length > 0) {
      return fieldMessages.join(" · ");
    }
    return error.response?.message || error.message || "The request failed.";
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong.";
}
